/**
 * Validates file size against MIME type restrictions
 * @param {File} file - The file to validate
 * @param {Object} maxFileSize - Configuration object with MIME type patterns and size limits in MB
 * @returns {Object} Validation result with { isValid: boolean, errorMessage?: string }
 */
const validateFileSize = (file, maxFileSize) => {
  if (!maxFileSize || Object.keys(maxFileSize).length === 0) {
    return {
      isValid: true
    };
  }
  const fileMimeType = file.type;
  const fileSizeInMB = file.size / (1024 * 1024); // Convert bytes to MB

  // If file type is undefined/null, reject the file as it may be corrupted or dangerous
  if (!fileMimeType) {
    return {
      isValid: false,
      errorMessage: 'File MIME type is undefined. Upload cannot be completed.'
    };
  }

  // Find matching MIME type pattern
  let maxSizeMB = null;
  let matchedPattern = null;
  Object.entries(maxFileSize).some(([mimePattern, sizeLimit]) => {
    if (mimePattern === fileMimeType) {
      // Exact match
      maxSizeMB = sizeLimit;
      matchedPattern = mimePattern;
      return true;
    }
    if (mimePattern.endsWith('/*')) {
      // Wildcard match (e.g., 'image/*' matches 'image/png')
      const baseType = mimePattern.slice(0, -1); // Remove the '*'
      if (fileMimeType.startsWith(baseType)) {
        maxSizeMB = sizeLimit;
        matchedPattern = mimePattern;
        return true;
      }
    }
    return false;
  });
  if (maxSizeMB === null) {
    // No matching pattern found, file is allowed
    return {
      isValid: true
    };
  }
  if (fileSizeInMB > maxSizeMB) {
    return {
      isValid: false,
      errorMessage: `File size (${fileSizeInMB.toFixed(2)} MB) exceeds the maximum allowed size ` + `(${maxSizeMB} MB) for ${matchedPattern} files.`
    };
  }
  return {
    isValid: true
  };
};

/**
 * Validates multiple files against MIME type restrictions and provides comprehensive feedback
 * @param {File[]} files - Array of files to validate
 * @param {Object} maxFileSize - Configuration object with MIME type patterns and size limits in MB
 * @returns {Object} Validation result with:
 *   {
 *     isValid: boolean,
 *     errorMessage?: string,
 *     violations?: Array
 *   }
 */
const validateMultipleFiles = (files, maxFileSize) => {
  if (!maxFileSize || Object.keys(maxFileSize).length === 0) {
    return {
      isValid: true
    };
  }
  const violations = [];
  files.forEach(file => {
    const validation = validateFileSize(file, maxFileSize);
    if (!validation.isValid) {
      violations.push({
        filename: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2),
        mimeType: file.type,
        errorMessage: validation.errorMessage
      });
    }
  });
  if (violations.length === 0) {
    return {
      isValid: true
    };
  }

  // Create comprehensive error message
  const part1 = 'Upload cannot be completed!\n\n';
  const part2 = 'These files are too large:\n';
  let errorMessage = part1 + part2;

  // Group violations by MIME type to show limits
  const mimeTypeLimits = {};
  violations.forEach(violation => {
    errorMessage += `• ${violation.filename} (${violation.size} MB)\n`;

    // Find the max size limit for this MIME type
    let maxSizeMB = null;
    let matchedPattern = null;
    Object.entries(maxFileSize).some(([mimePattern, sizeLimit]) => {
      if (mimePattern === violation.mimeType) {
        maxSizeMB = sizeLimit;
        matchedPattern = mimePattern;
        return true;
      }
      if (mimePattern.endsWith('/*')) {
        const baseType = mimePattern.slice(0, -1);
        if (violation.mimeType.startsWith(baseType)) {
          maxSizeMB = sizeLimit;
          matchedPattern = mimePattern;
          return true;
        }
      }
      return false;
    });
    if (maxSizeMB !== null) {
      mimeTypeLimits[matchedPattern] = maxSizeMB;
    }
  });

  // Add MIME type limits section
  if (Object.keys(mimeTypeLimits).length > 0) {
    errorMessage += '\nMaximum file sizes:\n';
    Object.entries(mimeTypeLimits).forEach(([mimeType, limit]) => {
      errorMessage += `• ${mimeType}: ${limit} MB\n`;
    });
  }

  // Add instruction
  errorMessage += '\nPlease try again with smaller file(s).';
  return {
    isValid: false,
    errorMessage: errorMessage.trim(),
    violations
  };
};
export default {
  validateFileSize,
  validateMultipleFiles
};